/**
 * Guests on the kal shape. Reads, archive, restore, delete, merge and stats go through GraphQL (services/guestSdk.ts);
 * create and update go through REST because the form saves tags and plus-ones together and gets the non-blocking
 * duplicate phone/email warnings back (the same split Kalligo's dashboard uses). Data is kept in project-partitioned
 * in-memory caches, never in localStorage: switching project never shows another project's guests.
 *
 * Two lists: loadGuests() is the ACTIVE guests (what stats, pickers and the dashboard see), loadArchivedGuests() the
 * archived ones (the archived view, restore, permanent delete). The archived flag comes from the server.
 */
import type { Guest, GuestFilter, GuestStats, GuestTag, PlusOne, RSVPStatus, GuestGroup } from '../types/Guests';
import { apiDelete, apiGet, apiPost, apiPut, planningPath } from './PlanningApiClient';
import { warningsFromSaveResponse, GUEST_SAVE_WARNINGS_EVENT } from './GuestContactCheckService';
import { sdk, type GuestRow } from './guestSdk';
import { lifecycleStatus } from '../utils/guestArchive';
import { countByRsvpStatus, effectiveRsvpStatus } from '../utils/guestRsvpStatus';
import { ProjectScopedCache } from './projectScopedCache';

const guestsCache = new ProjectScopedCache<Guest[]>(() => []);
const archivedGuestsCache = new ProjectScopedCache<Guest[]>(() => []);
const archivedLoadFailedCache = new ProjectScopedCache<boolean>(() => false);
const guestTagsCache = new ProjectScopedCache<GuestTag[]>(() => []);
const guestGroupsCache = new ProjectScopedCache<GuestGroup[]>(() => []);

/** True when the last refresh could not load this project's archived guests (the active list loaded fine). */
export const didArchivedGuestsLoadFail = (): boolean => archivedLoadFailedCache.load();

/** ACTIVE guests of the active project. */
export const loadGuests = (): Guest[] => guestsCache.load();
/** ARCHIVED guests of the active project: only for the archived view, restore and permanent delete. */
export const loadArchivedGuests = (): Guest[] => archivedGuestsCache.load();
const loadAllGuests = (): Guest[] => [...guestsCache.load(), ...archivedGuestsCache.load()];
const loadAllGuestsFor = (key: string): Guest[] => [...guestsCache.loadFor(key), ...archivedGuestsCache.loadFor(key)];

/**
 * Takes the FULL list and splits it by the archived flag into the two caches. `key` is the project the request was
 * made for: a request that finishes after a project switch still lands in ITS project, never in the one now open.
 */
const saveGuestsFor = (key: string, guests: Guest[]): void => {
  guestsCache.saveFor(key, guests.filter(g => g.archived !== true));
  archivedGuestsCache.saveFor(key, guests.filter(g => g.archived === true));
};
export const saveGuests = (guests: Guest[]): void => saveGuestsFor(guestsCache.currentKey(), guests);

export const loadGuestTags = (): GuestTag[] => guestTagsCache.load();
export const loadGuestGroups = (): GuestGroup[] => guestGroupsCache.load();

// ── mapping ──

function mapRow(row: GuestRow, archived: boolean): Guest {
  return {
    id: row.id,
    reference_id: row.reference_id ?? null,
    name: row.name ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    avatar: row.avatar ?? '',
    tags: (row.tags ?? []) as GuestTag[],
    status: lifecycleStatus(row.status),
    archived,
    rsvp_status: (row.rsvp_status ?? 'pending') as RSVPStatus,
    rsvp_status_derived: (row.rsvp_status_derived ?? null) as RSVPStatus | null,
    rsvp_date: row.rsvp_date ?? null,
    last_login_at: row.last_login_at ?? null,
    plus_ones: (row.plus_ones ?? []).map((p): PlusOne => ({
      id: p.id,
      name: p.name ?? '',
      relationship: p.relationship,
      dietaryRestrictions: p.dietary_restrictions ?? undefined,
      age: p.age ?? undefined,
    })),
    household_id: row.household_id ?? null,
    role: row.role ?? null,
    is_honoree: row.is_honoree ?? false,
    party_of: row.party_of ?? null,
    party_role: row.party_role ?? null,
    notes: row.notes ?? undefined,
    side: row.side ?? undefined,
    dietary_restrictions: row.dietary_restrictions ?? [],
    created_at: new Date(row.created_at),
    updated_at: new Date(row.updated_at),
  };
}

/** The shape the REST create/update routes accept (guests carry no project id, money or credential here). */
function toWriteBody(g: Partial<Guest>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const copy = (key: keyof Guest, as: string = key as string) => { if (g[key] !== undefined) body[as] = g[key]; };
  copy('name'); copy('email'); copy('phone'); copy('avatar'); copy('tags'); copy('rsvp_status'); copy('rsvp_date');
  copy('notes'); copy('side'); copy('dietary_restrictions'); copy('role'); copy('is_honoree'); copy('party_of'); copy('party_role');
  if (g.status !== undefined) body.status = lifecycleStatus(g.status);
  if (g.plus_ones !== undefined) {
    body.plus_ones = g.plus_ones.map(p => ({
      id: p.id,
      name: p.name,
      relationship: p.relationship,
      ...(p.email ? { email: p.email } : {}),
      ...(p.dietaryRestrictions ? { dietary_restrictions: p.dietaryRestrictions } : {}),
      ...(p.age !== undefined ? { age: p.age } : {}),
    }));
  }
  if (body.party_of === '') body.party_of = null;
  if (body.role === '') body.role = null;
  if (body.phone === '') body.phone = null;
  return body;
}

function announceSaveWarnings(response: unknown, guestName: string): void {
  const warnings = warningsFromSaveResponse(response);
  if (warnings.length === 0) return;
  window.dispatchEvent(new CustomEvent(GUEST_SAVE_WARNINGS_EVENT, { detail: { guestName, messages: warnings.map(w => w.message) } }));
}

// ── refresh ──

/**
 * Pulls this project's guests into the cache. A success, even with zero rows, REPLACES the project's cache; only a
 * thrown request keeps the previous one. The archived list failing never hides the active guests.
 */
const refreshing = new Map<string, Promise<Guest[]>>();

/**
 * One refresh per project at a time: the page, the dashboard and the background sync all ask for the same list when
 * the app opens, and the API answers the first; the others share its result instead of repeating the request.
 */
export const refreshGuestsFromApi = (): Promise<Guest[]> => {
  const key = guestsCache.currentKey();
  const running = refreshing.get(key);
  if (running) return running;
  const started = refreshGuestsNow().finally(() => { refreshing.delete(key); });
  refreshing.set(key, started);
  return started;
};

const refreshGuestsNow = async (): Promise<Guest[]> => {
  const key = guestsCache.currentKey(); // the project this refresh is for, fixed before any await
  const active = await sdk.Guests();
  let archived: GuestRow[] = [];
  let archivedFailed = false;
  try {
    archived = (await sdk.ArchivedGuests()).guests ?? [];
  } catch {
    archivedFailed = true;
  }
  archivedLoadFailedCache.saveFor(key, archivedFailed);
  saveGuestsFor(key, [
    ...(active.guests ?? []).map(row => mapRow(row, false)),
    ...archived.map(row => mapRow(row, true)),
  ]);
  return guestsCache.loadFor(key);
};

interface ApiTag { id: string; label: string; color: string; category: GuestTag['category'] }

export const refreshGuestTagsFromApi = async (): Promise<GuestTag[]> => {
  const key = guestTagsCache.currentKey();
  const res = await apiGet<{ tags: ApiTag[] }>(planningPath('/guest-tags'));
  const tags = (res?.tags ?? []).map(t => ({ id: t.id, label: t.label, color: t.color, category: t.category }));
  guestTagsCache.saveFor(key, tags);
  return tags;
};

export const createGuestTagApi = async (tag: GuestTag): Promise<GuestTag> => {
  const key = guestTagsCache.currentKey();
  const created = await apiPost<ApiTag>(planningPath('/guest-tags'), { id: tag.id, label: tag.label, color: tag.color, category: tag.category });
  const mapped: GuestTag = { id: created.id, label: created.label, color: created.color, category: created.category };
  guestTagsCache.saveFor(key, [...guestTagsCache.loadFor(key), mapped]);
  return mapped;
};

export const deleteGuestTagApi = async (id: string): Promise<void> => {
  const key = guestTagsCache.currentKey();
  await apiDelete(planningPath(`/guest-tags/${encodeURIComponent(id)}`));
  guestTagsCache.saveFor(key, guestTagsCache.loadFor(key).filter(t => t.id !== id));
};

// ── writes ──

/**
 * Creates a guest. Awaitable: a rejection means nothing was saved. The server's own id (GS-###) is used, never one
 * made here. Duplicate phone/email warnings come back with the saved guest and are announced; they never block.
 */
export const createGuest = async (data: Omit<Guest, 'id' | 'created_at' | 'updated_at'>): Promise<Guest> => {
  const key = guestsCache.currentKey();
  const saved = await apiPost<GuestRow & { warnings?: unknown }>(planningPath('/guests'), toWriteBody(data));
  announceSaveWarnings(saved, data.name);
  const guest = mapRow({ ...saved, tags: saved.tags ?? [], plus_ones: saved.plus_ones ?? [] } as GuestRow, false);
  saveGuestsFor(key, [...loadAllGuestsFor(key).filter(g => g.id !== guest.id), guest]);
  return guest;
};

/** Updates a guest. Optimistic in the cache, rolled back when the server refuses. */
export const updateGuest = async (id: string, updates: Partial<Guest>): Promise<Guest | null> => {
  const key = guestsCache.currentKey();
  const guests = loadAllGuestsFor(key);
  const previous = guests.find(g => g.id === id);
  if (!previous) return null;
  const optimistic: Guest = { ...previous, ...updates, updated_at: new Date() };
  saveGuestsFor(key, guests.map(g => (g.id === id ? optimistic : g)));
  try {
    const saved = await apiPut<GuestRow & { warnings?: unknown }>(planningPath(`/guests/${encodeURIComponent(id)}`), toWriteBody(updates));
    announceSaveWarnings(saved, optimistic.name);
    const fresh = mapRow({ ...saved, tags: saved.tags ?? [], plus_ones: saved.plus_ones ?? [] } as GuestRow, previous.archived === true);
    saveGuestsFor(key, loadAllGuestsFor(key).map(g => (g.id === id ? fresh : g)));
    return fresh;
  } catch (err) {
    saveGuestsFor(key, loadAllGuestsFor(key).map(g => (g.id === id ? previous : g)));
    throw err;
  }
};

const setCachedArchived = (key: string, id: string, archived: boolean): void => {
  saveGuestsFor(key, loadAllGuestsFor(key).map(g => (g.id === id ? { ...g, archived, updated_at: new Date() } : g)));
};

/** Archive (soft delete). Rejects on failure; only then is the cached guest flagged. */
export const archiveGuest = async (id: string): Promise<void> => {
  const key = guestsCache.currentKey();
  await sdk.DeleteGuest({ ref: id });
  setCachedArchived(key, id, true);
};

export const restoreGuest = async (id: string): Promise<void> => {
  const key = guestsCache.currentKey();
  await sdk.RestoreGuest({ ref: id });
  setCachedArchived(key, id, false);
};

/** Irreversible. The API only allows it for an already archived guest and refuses while other records use the guest. */
export const deleteGuestPermanentOrThrow = async (id: string): Promise<void> => {
  const key = guestsCache.currentKey();
  const res = await sdk.DeleteGuestPermanent({ ref: id });
  if (res?.deleteGuestPermanent === false) throw new Error('The guest could not be deleted. Please try again.');
  saveGuestsFor(key, loadAllGuestsFor(key).filter(g => g.id !== id));
};

/** One server-side transaction: the keeper survives, every other guest is archived and its answers are repointed. */
export const mergeGuests = async (keepId: string, loseIds: string[]): Promise<{ success: boolean; error?: string }> => {
  try {
    await sdk.MergeGuests({ keep_ref: keepId, lose_refs: loseIds });
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Failed to merge guests.' };
  }
};

/** Detail-only fetch of the portal invite code (a guest-facing credential, never part of the list). Null when redacted. */
export const fetchGuestInviteCode = async (guestId: string): Promise<string | null> => {
  try {
    return (await sdk.Guest({ ref: guestId }))?.guest?.invite_code ?? null;
  } catch {
    return null;
  }
};

// ── lookups ──

export const getGuestById = (id: string): Guest | null => loadAllGuests().find(g => g.id === id) ?? null;

/** Display name of a guest looked up by id, archived guests included. */
export const guestNameById = (id: string | null | undefined, fallback = 'Unknown Guest'): string =>
  (id ? getGuestById(id)?.name : undefined) ?? fallback;

/** The guests who name `guestId` as their inviter (party_of), archived ones included. */
export const loadPlusOnesOf = (guestId: string): Guest[] => loadAllGuests().filter(g => g.party_of === guestId);

export const filterGuests = (guests: Guest[], filter: GuestFilter): Guest[] =>
  guests.filter(guest => {
    if (filter.status?.length && !filter.status.includes(guest.status)) return false;
    if (filter.rsvpStatus?.length && !filter.rsvpStatus.includes(effectiveRsvpStatus(guest))) return false;
    if (filter.tags?.length && !filter.tags.some(tagId => guest.tags.some(tag => tag.id === tagId))) return false;
    if (filter.searchTerm) {
      const q = filter.searchTerm.toLowerCase();
      const hit = guest.name.toLowerCase().includes(q)
        || guest.email.toLowerCase().includes(q)
        || (guest.phone ?? '').toLowerCase().includes(q)
        || guest.tags.some(tag => tag.label.toLowerCase().includes(q));
      if (!hit) return false;
    }
    return true;
  });

// ── stats ──

/** Project-wide RSVP breakdown and plus-one total from the API's own single SQL aggregation. */
export interface ServerGuestStats {
  totalGuests: number;
  confirmed: number;
  pending: number;
  declined: number;
  otherStatus: number;
  totalPlusOnes: number;
}

const serverStatsCache = new ProjectScopedCache<ServerGuestStats | null>(() => null);
export const loadServerGuestStats = (): ServerGuestStats | null => serverStatsCache.load();

/** Null on any failure, so callers fall back to counting the cached guests rather than throwing on a routine refresh. */
export const fetchServerGuestStats = async (): Promise<ServerGuestStats | null> => {
  const key = serverStatsCache.currentKey();
  try {
    const s = (await sdk.GuestStats())?.guestStats;
    if (!s) return null;
    const stats: ServerGuestStats = {
      totalGuests: s.total_guests ?? 0,
      confirmed: s.confirmed ?? 0,
      pending: s.pending ?? 0,
      declined: s.declined ?? 0,
      otherStatus: s.other_status ?? 0,
      totalPlusOnes: s.total_plus_ones ?? 0,
    };
    serverStatsCache.saveFor(key, stats);
    return stats;
  } catch (err) {
    console.error('fetchServerGuestStats failed, falling back to counting the cached guests', err);
    return null;
  }
};

/** The dashboard's "invited" figures. `server` (when given) wins over counting the cached rows. */
export const getGuestStats = (guests: Guest[], capacityOverride?: number, server?: ServerGuestStats | null): GuestStats => {
  const local = countByRsvpStatus(guests);
  const totalGuests = server?.totalGuests ?? guests.length;
  const confirmedGuests = server?.confirmed ?? local.confirmed;
  const pendingRSVPs = server?.pending ?? local.pending;
  const declinedGuests = server?.declined ?? local.declined;
  const awaitingPlusOne = local.awaiting_plus_one;
  const otherStatus = server?.otherStatus ?? Math.max(0, totalGuests - confirmedGuests - pendingRSVPs - declinedGuests - awaitingPlusOne);
  const totalPlusOnes = server?.totalPlusOnes ?? guests.reduce((sum, g) => sum + (g.plus_ones?.length ?? 0), 0);
  const respondedGuests = totalGuests - pendingRSVPs - awaitingPlusOne;
  const responseRate = respondedGuests > 0 && totalGuests > 0 ? (respondedGuests / totalGuests) * 100 : 0;
  const capacityLimit = capacityOverride || undefined;
  const overCapacity = capacityLimit ? totalGuests + totalPlusOnes > capacityLimit : false;
  return {
    totalGuests, confirmedGuests, pendingRSVPs, declinedGuests, awaitingPlusOne, otherStatus, totalPlusOnes,
    responseRate, capacityLimit, overCapacity,
  };
};
